function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Container, CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, Delta, Pending } from "../../components/analytics-kit";
import publishSearch from "../../functions/publish-search";
import publishEvent from "../../functions/publish-event";
import { Loader } from "../../components/loaders";
import ServiceFactory from "../../service";
import { MDCTextInput, MDCFileUpload } from "../../../jsx/components/mdc-components";
import { AlertMessage } from "../../ui/alert";
import endpoints from "../../endpoints";
import account from "../../data/account";
import { forecastLens } from "../../functions/tenant-features";
import { ForecastsHeader } from "./forecast-common";

/*
 * Similarity Forecast — redesigned landing (FORECASTING_PROGRAM.md Part 2;
 * mockup similarity-forecast-redesign.html). Two lenses, one pipeline:
 * the tenant's ForecastLens registry attribute (broker tenantConfig)
 * frames the intro copy — manufacturer ("test a design before you build
 * it") vs retail ("will it sell in your stores?"). The form + runs table
 * are lens-agnostic.
 *
 * Contract (staging-probed 2026-08-10):
 *   GET  /forecast/product-similarity-forecast            list runs
 *   POST /forecast/product-similarity-forecast            multipart upload;
 *        headers X-APP-ID, X-NAME, X-DESCRIPTION, X-CATEGORY (category
 *        hint drives comparable matching — G-register note)
 *   GET  .../{id}/refresh                                 queue re-run
 *   DELETE .../{id}                                       delete run
 *
 * Status enum (shipped backend): Pending → Processing → Matching →
 * Forecasting → Recommending → Completed / Error. Mapped to honest labels
 * below; the list polls every 10s while any run is non-terminal.
 */

const STATUS_LABELS = {
  pending: {
    label: "Analyzing image",
    cls: "run"
  },
  processing: {
    label: "Analyzing image",
    cls: "run"
  },
  matching: {
    label: "Finding comparables",
    cls: "run"
  },
  forecasting: {
    label: "Forecasting",
    cls: "run"
  },
  recommending: {
    label: "Recommendations",
    cls: "run"
  },
  completed: {
    label: "Complete",
    cls: "ok"
  },
  error: {
    label: "Needs retry",
    cls: "warn"
  }
};
export function statusInfo(status) {
  return STATUS_LABELS[String(status || "").toLowerCase()] || {
    label: status || "—",
    cls: "run"
  };
}
export function isTerminal(status) {
  const s = String(status || "").toLowerCase();
  return s === "completed" || s === "error";
}
export function isLegacyRun(run) {
  // Pre-2026 runs came from the old engine (similarity 0.0 / negative
  // units bugs, since fixed) — badge them, never show unlabeled (gap G7).
  const d = run && (run.createdDate || run.CreatedDate);
  return d ? String(d) < "2026" : false;
}
const INTRO = {
  manufacturer: {
    heading: "Test a design before you build it.",
    body: "Upload a product image — a render, a prototype shot, a market-week candidate — and we'll forecast demand from how the closest products in your market actually performed. Results in a few minutes."
  },
  retail: {
    heading: "Will it sell in your stores?",
    body: "Upload a vendor product photo and we'll score its fit against what already sells — by store, by market — using your own sales history. Results in a few minutes."
  }
};
class MainComponent extends React.Component {
  constructor(props) {
    super(props);
    // ── new-forecast form ──
    _defineProperty(this, "onFocus", () => this.setState({
      message: null,
      messageType: ""
    }));
    _defineProperty(this, "onBeforeUpload", files => {
      if (this.count > 0) return false;
      const {
        name,
        category,
        description
      } = this.state;
      // Tenant guard (UX note 08-14 §3): the constructor-captured appId can
      // be empty when the account cache wasn't ready at mount — a headerless
      // write reaches the backend as [TenantResolution] EMPTY and 500s.
      // Re-resolve LIVE and refuse to fire without a workspace identity.
      const appId = (account.get() || {}).tenantId || this.state.appId;
      if (!appId) {
        this.count = 0;
        this.upload.reset();
        this.setState({
          message: "Your session isn't ready — reload the page and try again.",
          messageType: "error"
        });
        return false;
      }
      if (!name.trim()) {
        this.count = 0;
        this.upload.reset();
        this.setState({
          message: "Error: a product name is required.",
          messageType: "error"
        });
        return false;
      }
      this.upload.headers = [{
        key: "authorization",
        value: ""
      }, {
        key: "X-APP-ID",
        value: appId
      }, {
        key: "X-NAME",
        value: name.trim()
      }, {
        key: "X-DESCRIPTION",
        value: description.trim()
      }, {
        key: "X-CATEGORY",
        value: category.trim()
      }, {
        key: "X-PRICE",
        value: this.state.price.trim()
      }];
      this.count = 1;
      this.setState({
        uploading: true
      });
      this.upload.resume(files);
    });
    _defineProperty(this, "onUploadError", () => {
      this.count = 0;
      this.setState({
        uploading: false,
        message: "Error uploading image — try again.",
        messageType: "error"
      });
    });
    _defineProperty(this, "onUploadComplete", () => {
      this.count = 0;
      this.setState({
        uploading: false,
        showSuccess: true,
        message: null,
        messageType: ""
      });
      if (this.toast) this.toast.show("Forecast started — it'll appear in the list below.");
      setTimeout(() => this.load(), 2500);
    });
    _defineProperty(this, "onUploadProgressComplete", () => {
      this.setState({
        message: "Processing uploaded image…",
        messageType: ""
      });
    });
    _defineProperty(this, "resetForm", () => {
      this.count = 0;
      this.setState({
        showSuccess: false,
        name: "",
        category: "",
        price: "",
        description: "",
        message: null,
        messageType: ""
      });
    });
    // ── run actions ──
    _defineProperty(this, "sessionReady", () => {
      if ((account.get() || {}).tenantId) return true;
      if (this.toast) this.toast.show("Session expired — reload the page and sign in again.");
      return false;
    });
    _defineProperty(this, "onRetry", run => {
      if (!this.sessionReady()) return;
      const Service = ServiceFactory.getType(`/forecast/product-similarity-forecast/${String(run.id)}/refresh`, "forecast");
      new Service().get({}, err => {
        if (this.toast) this.toast.show(err ? "Error queuing the re-run." : "Re-run queued — status updates below.");
        if (!err) setTimeout(() => this.load(), 2000);
      });
    });
    _defineProperty(this, "onDelete", run => {
      if (!this.sessionReady()) return;
      const Service = ServiceFactory.getType("/forecast/product-similarity-forecast", "forecast");
      new Service().delete(String(run.id), err => {
        if (this.toast) this.toast.show(err ? "Error deleting the forecast." : "Forecast deleted.");
        this.setState({
          confirmDelete: null
        });
        if (!err) this.load();
      });
    });
    _defineProperty(this, "openRun", run => {
      if (String(run.status).toLowerCase() === "completed") {
        this.props.history.push(`/Forecast/Similarity/Detail/${String(run.id)}`);
      }
    });
    // "Test a new design" opens as its own sub-page in the upload-page format
    // (back button, numbered step cards, boxed inputs), like Create audience / Upload markets.
    _defineProperty(this, "closeForm", () => {
      this.setState({
        showForm: false
      });
      if (/[?&]new=1\b/.test(location.search || "")) this.props.history.replace("/Forecast/Similarity");
    });
    const accountData = account.get() || {};
    this.state = {
      runs: null,
      name: "",
      category: "",
      price: "",
      description: "",
      // "Test a new design" on the Forecasts header lands here with ?new=1.
      showForm: typeof location !== "undefined" && /[?&]new=1\b/.test(location.search || ""),
      uploading: false,
      showSuccess: false,
      message: null,
      messageType: "",
      confirmDelete: null,
      appId: accountData.tenantId,
      postUrl: endpoints.forecast.root + "/forecast/product-similarity-forecast"
    };
    this._alive = true;
    this.count = 0;
  }
  componentDidMount() {
    publishSearch("Similarity Forecast");
    publishEvent("reset-search", {});
    this.toast = document.querySelector("paper-toast");
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    if (this._poll) clearTimeout(this._poll);
  }
  async load() {
    try {
      const Service = ServiceFactory.getType("/forecast/product-similarity-forecast?$top=50&$orderByDesc=CreatedDate", "forecast");
      const data = await new Service().getAsync();
      if (!this._alive) return;
      const runs = data && data.items || [];
      this.setState({
        runs
      });
      // Honest progress: poll while anything is still running.
      if (runs.some(r => !isTerminal(r.status))) {
        if (this._poll) clearTimeout(this._poll);
        this._poll = setTimeout(() => this.load(), 10000);
      }
    } catch (e) {
      if (this._alive) this.setState({
        runs: []
      });
    }
  }
  renderFormPage() {
    const {
      showSuccess,
      uploading,
      message,
      messageType
    } = this.state;
    const field = (id, label, hint, key, opts = {}) => /*#__PURE__*/React.createElement("label", {
      className: "aup-field",
      htmlFor: id
    }, /*#__PURE__*/React.createElement("span", {
      className: "aup-label"
    }, label, opts.optional ? /*#__PURE__*/React.createElement("span", {
      className: "aup-opt"
    }, " (optional)") : null), /*#__PURE__*/React.createElement("input", {
      id: id,
      className: "aup-input",
      type: "text",
      value: this.state[key],
      placeholder: hint,
      onChange: e => this.setState({
        [key]: opts.money ? e.target.value.replace(/[^0-9.]/g, "") : e.target.value
      }),
      onFocus: this.onFocus
    }));
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view ec-view ecc-view aup-view fc-view"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-top"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-back",
      "aria-label": "Back to forecasts",
      onClick: this.closeForm
    }, /*#__PURE__*/React.createElement("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 16 16",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M10 3L5 8l5 5",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.8",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "ecc-titleblock"
    }, /*#__PURE__*/React.createElement("span", {
      className: "aup-title"
    }, "Test a new design"), /*#__PURE__*/React.createElement("span", {
      className: "ecc-meta"
    }, "Upload a product image and we forecast its demand from how the closest products in your market sold.")), /*#__PURE__*/React.createElement("span", {
      className: "ecc-spacer"
    })), showSuccess ? /*#__PURE__*/React.createElement("section", {
      className: "aup-card aup-done"
    }, /*#__PURE__*/React.createElement("b", null, "Your forecast is running"), /*#__PURE__*/React.createElement("span", null, "It usually takes 2 to 5 minutes. You can leave this page; the result appears in your list of new designs."), /*#__PURE__*/React.createElement("span", {
      className: "aup-actions"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      onClick: () => {
        this.resetForm();
        this.closeForm();
      }
    }, "Back to new designs"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      onClick: this.resetForm
    }, "Test another design"))) : /*#__PURE__*/React.createElement("div", {
      className: "aup-steps"
    }, /*#__PURE__*/React.createElement("section", {
      className: "aup-card"
    }, /*#__PURE__*/React.createElement("b", {
      className: "aup-h"
    }, "1 \xB7 Describe the design"), /*#__PURE__*/React.createElement("div", {
      className: "fc-form-grid"
    }, field("simf-name", "Product name", "For example: Tobin sofa, version 2", "name"), field("simf-cat", "Category", "For example: Sofas. Helps find the closest products", "category"), field("simf-price", "Proposed price", "For example: 2400. Shows where it sits on price", "price", {
      optional: true,
      money: true
    }), field("simf-notes", "Notes", "Materials, finish, who it is for", "description", {
      optional: true
    }))), /*#__PURE__*/React.createElement("section", {
      className: "aup-card"
    }, /*#__PURE__*/React.createElement("b", {
      className: "aup-h"
    }, "2 \xB7 Upload a product image"), /*#__PURE__*/React.createElement("span", {
      className: "aup-hint"
    }, "A render, a prototype photo or a sample shot. JPG or PNG."), /*#__PURE__*/React.createElement(MDCFileUpload, {
      cls: "aup-drop",
      singleFileUpload: true,
      postUrl: this.state.postUrl,
      onBeforeUpload: this.onBeforeUpload,
      onUploadError: this.onUploadError,
      onUploadComplete: this.onUploadComplete,
      onUploadProgressComplete: this.onUploadProgressComplete,
      ref: ref => this.upload = ref,
      accept: ".jpg,.png"
    }), uploading && /*#__PURE__*/React.createElement("div", {
      className: "aup-msg"
    }, "Uploading\u2026"), message && /*#__PURE__*/React.createElement("div", {
      className: "aup-msg" + (messageType === "error" ? " bad" : "")
    }, String(message).replace(/^Error:\s*/, "")))));
  }
  render() {
    const {
      runs,
      showForm,
      confirmDelete
    } = this.state;
    if (showForm) return this.renderFormPage();
    const lens = forecastLens();
    const intro = INTRO[lens] || INTRO.manufacturer;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view simf-view wa-view fc-view"
    }, /*#__PURE__*/React.createElement(ForecastsHeader, {
      active: "designs",
      designs: runs ? runs.length : null
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Tested designs",
      hint: runs && runs.some(r => !isTerminal(r.status)) ? "steps: analyzing image, finding comparables, forecasting, recommendations · usually 2 to 5 minutes · this list refreshes itself" : "completed forecasts open to their results"
    }, !runs ? /*#__PURE__*/React.createElement(Pending, null) : !runs.length ? /*#__PURE__*/React.createElement(Empty, null, "No designs tested yet. Use \"Test a new design\" to run the first one.") : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null), /*#__PURE__*/React.createElement("th", null, "Design"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", null, "Started"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }))), /*#__PURE__*/React.createElement("tbody", null, runs.map(run => {
      const st = statusInfo(run.status);
      const done = String(run.status).toLowerCase() === "completed";
      const failed = String(run.status).toLowerCase() === "error";
      const isConfirm = confirmDelete === String(run.id);
      return /*#__PURE__*/React.createElement("tr", {
        key: String(run.id),
        className: done ? "clickable" : "",
        onClick: () => this.openRun(run)
      }, /*#__PURE__*/React.createElement("td", {
        style: {
          width: "64px"
        }
      }, /*#__PURE__*/React.createElement("img", {
        src: run.imageLink,
        alt: "",
        style: {
          width: "52px",
          height: "42px",
          objectFit: "cover",
          borderRadius: "4px",
          background: "#f0f1f3"
        },
        onError: ({
          currentTarget
        }) => {
          currentTarget.style.visibility = "hidden";
        }
      })), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, run.name), run.category ? /*#__PURE__*/React.createElement("span", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, run.category) : null, isLegacyRun(run) && /*#__PURE__*/React.createElement("span", {
        style: {
          marginLeft: "6px"
        }
      }, /*#__PURE__*/React.createElement(Pill, {
        tone: "warning"
      }, "Legacy engine"))), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
        tone: pillTone(st.cls)
      }, st.label)), /*#__PURE__*/React.createElement("td", null, run.createdDate ? new Date(String(run.createdDate).slice(0, 10) + "T12:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      }) : "-"), /*#__PURE__*/React.createElement("td", {
        className: "n",
        style: {
          whiteSpace: "nowrap"
        },
        onClick: e => e.stopPropagation()
      }, /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-flex",
          gap: 10
        }
      }, done && /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: () => this.openRun(run)
      }, "Open"), failed && /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: () => this.onRetry(run)
      }, "Retry"), isConfirm ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        style: {
          color: "var(--plk-danger-text)"
        },
        onClick: () => this.onDelete(run)
      }, "Confirm delete"), /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: () => this.setState({
          confirmDelete: null
        })
      }, "Cancel")) : /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink",
        onClick: () => this.setState({
          confirmDelete: String(run.id)
        })
      }, "Remove"))));
    })))))));
  }
}
const Routable = withRouter(MainComponent);
// simf status classes → kit pill tones
export function pillTone(cls) {
  const c = String(cls || "");
  return /ok|done|success/.test(c) ? "success" : /warn/.test(c) ? "warning" : /err|fail|danger/.test(c) ? "danger" : "info";
}
export class ForecastSimilarityViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Routable, {
      params: this.props.params
    });
  }
}
export default {
  ForecastSimilarityViewComponent
};